import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../material/material.module';
import {
  NegotiationControllerService,
  Negotiation,
  UserRef,
  ChatNegotiationControllerService,
  SendMessageRequest,
} from '../../../core/generated';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';
import { UserPreferencesCacheService } from '../../services/user-preferences-cache.service';
import { CommonModule } from '@angular/common';
import { TipsDialogComponent } from '../../tips/tips-dialog.component';

export interface DialogData {
  initiatorCpfCnpj?: string;
  recipientCpfCnpj?: string;
  referenceId?: any;
  referenceType?: 'serviceOffering' | 'serviceDemand' | 'resourceOffering' | 'resourceDemand';
}

@Component({
    selector: 'app-dialog-register-negotiation',
    standalone: true,
    imports: [MaterialModule, ReactiveFormsModule, CommonModule],
    templateUrl: './dialog-register-negotiation.component.html',
    styleUrls: ['./dialog-register-negotiation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DialogRegisterNegotiationComponent implements OnInit {
  negotiationsService = inject(NegotiationControllerService);
  chatNegotiationsService = inject(ChatNegotiationControllerService);
  cd = inject(ChangeDetectorRef);
  deviceService = inject(DeviceDetectorService);
  userPreferencesCacheService = inject(UserPreferencesCacheService);
  fb = inject(FormBuilder);
  dialogRef = inject(MatDialogRef<DialogRegisterNegotiationComponent>);
  data = inject(MAT_DIALOG_DATA) as DialogData;
  dialog = inject(MatDialog);

  isMobile = this.deviceService.isMobile();
  user!: UserRef;
  negotiationForm: FormGroup;
  isLoading = false;

  referenceTypeOptions = [
    { value: 'serviceOffering', label: 'Oferta de Serviço' },
    { value: 'serviceDemand', label: 'Demanda de Serviço' },
    { value: 'resourceOffering', label: 'Oferta de Recurso' },
    { value: 'resourceDemand', label: 'Demanda de Recurso' }
  ];

  constructor() {
    this.negotiationForm = this.fb.group({
      initialMessage: ['', [Validators.required, Validators.maxLength(500)]]
    });
  }

  async ngOnInit() {
    await this.loadUserProfile();
  }

  async loadUserProfile() {
    try {
      this.user = await firstValueFrom(this.userPreferencesCacheService.getUser());
    } catch (error) {
      console.error('Failed to load user profile', error);
    }
  }

  async onSubmit() {
    if (this.negotiationForm.valid && !this.isLoading && this.user?.cpf) {
      this.isLoading = true;
      this.cd.detectChanges();

      try {
        const formValue = this.negotiationForm.value;

        const createRequest: Negotiation = {
          initiatorCpfCnpj: this.data.initiatorCpfCnpj!,
          referenceId: this.data.referenceId,
          referenceType: this.data.referenceType!,
        };

        const negotiation = await firstValueFrom(
          this.negotiationsService.createNegotiation(createRequest)
        );

        if (formValue.initialMessage && negotiation.id) {
          const sendMessageRequest: SendMessageRequest = {
            negotiationId: negotiation.id,
            senderCpfCnpj: this.data.initiatorCpfCnpj!,
            message: formValue.initialMessage
          };

          await firstValueFrom(
            this.chatNegotiationsService.sendMessage(sendMessageRequest)
          );
        }

        this.dialogRef.close(negotiation);
      } catch (error) {
        this.openTipDialog('Negociação em andamento');
      } finally {
        this.isLoading = false;
        this.cd.detectChanges();
      }
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  getErrorMessage(fieldName: string): string {
    const field = this.negotiationForm.get(fieldName);

    if (field?.hasError('required')) {
      return 'Este campo é obrigatório';
    }

    if (field?.hasError('minlength')) {
      return 'CPF/CNPJ deve ter pelo menos 11 caracteres';
    }

    if (field?.hasError('maxlength')) {
      return 'Mensagem deve ter no máximo 500 caracteres';
    }

    if (field?.hasError('min')) {
      return 'ID de referência deve ser maior que 0';
    }

    return '';
  }

  openTipDialog(title: string) {
    this.dialog.open(TipsDialogComponent, {
      data: { title },
    });
  }
}
